import { useCallback, useEffect, useRef } from "react";

/**
 * Default debounce delay in milliseconds.
 * Used for autocomplete/typeahead fetch operations to avoid
 * excessive backend calls during rapid user input.
 */
export const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Returns a debounced version of the given callback.
 *
 * The returned function delays invoking `callback` until after `delay` ms
 * have elapsed since the last invocation. Useful for search/autocomplete
 * inputs that trigger backend fetches.
 *
 * - Automatically cleans up pending timers on unmount.
 * - Provides a `cancel()` method on the returned function to manually
 *   discard any pending invocation.
 * - The callback reference is always kept up-to-date (no stale closures).
 *
 * @param callback - The function to debounce
 * @param delay - Debounce delay in ms (default: {@link DEFAULT_DEBOUNCE_MS})
 * @returns A debounced function with an additional `cancel` method
 *
 * @example
 * ```tsx
 * const fetchSuggestions = useDebouncedCallback(async (text: string) => {
 *   const results = await api.search(text);
 *   setOptions(results);
 * });
 *
 * <input onChange={(e) => fetchSuggestions(e.target.value)} />
 * ```
 */
export function useDebouncedCallback<Args extends unknown[]>(
	callback: (...args: Args) => void,
	delay: number = DEFAULT_DEBOUNCE_MS
): ((...args: Args) => void) & { cancel: () => void } {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const callbackRef = useRef(callback);

	// Always keep the latest callback reference
	callbackRef.current = callback;

	const cancel = useCallback(() => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const debounced = useCallback(
		(...args: Args) => {
			cancel();
			timerRef.current = setTimeout(() => {
				timerRef.current = null;
				callbackRef.current(...args);
			}, delay);
		},
		[delay, cancel]
	);

	// Clean up on unmount
	useEffect(() => cancel, [cancel]);

	return Object.assign(debounced, { cancel });
}
